import { useTranslations } from "@zapier/i18n";
import { ChangeEvent, useState } from "react";

import {
  Button,
  Checkbox,
  Field,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
  TextInput,
} from "@zapier/design-system";

import {
  FolderTypeQueryState,
  getFolderUrl,
  useNotification,
} from "@zapier/utils";
import {
  getMessageFromNetworkError,
  useCreateFolder,
  useCreateSubfolder,
  useSupportedAssets,
} from "@zapier/client";
import {
  FolderActionInteractionOption,
  useEmitFolderActionEvent,
} from "@zapier/shared";
import { useRouter } from "next/navigation";
import styles from "./FolderCreationModal.module.css";

interface Props {
  // When set to true, this forces the sharing checkbox to be displayed and selectable
  isSharedSelectable?: boolean;
  isOpen: boolean;
  isShared?: boolean;
  parentId?: string;
  setIsOpen: (isOpen: boolean) => void;
  targetParentFolderName?: string;
}

interface ContentProps extends Props {
  onSubmit: (name: string, isShared: boolean) => void;
  onSubmitLoading: boolean;
}

function FolderCreationModalContent({
  isOpen,
  isShared,
  isSharedSelectable,
  onSubmit,
  onSubmitLoading,
  setIsOpen,
  targetParentFolderName,
}: ContentProps) {
  const t = useTranslations("folder-creation-modal");

  const [value, setValue] = useState("");
  const [touched, setTouched] = useState(false);
  const [isSharedChecked, setIsSharedChecked] = useState(isShared || false);
  const isValid = value.length > 0 && value.length <= 255;
  const errorMessage =
    touched && value.length === 0
      ? t("errors.name-required")
      : value.length > 255
        ? t("errors.name-too-long")
        : undefined;
  const { isMPFEnabled } = useSupportedAssets();

  const getHeaderKey = () => {
    if (targetParentFolderName) {
      if (isMPFEnabled) {
        return "headers.create-folder-in-parent";
      }
      return isSharedChecked
        ? "headers.create-shared-folder-in-parent"
        : "headers.create-personal-folder-in-parent";
    }

    if (isMPFEnabled) {
      return "headers.create-folder";
    }
    return isSharedChecked
      ? "headers.create-shared-folder"
      : "headers.create-personal-folder";
  };

  const { emit } = useEmitFolderActionEvent();
  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter */}
      <div
        role="form"
        onSubmit={() => onSubmit(value, isSharedChecked)}
        onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
        onKeyDown={
          // if the enter key is being pressed submit the form
          (event) => {
            if (event.key !== "Enter") return;
            if (onSubmitLoading) return;
            // Surface the required-name error on an empty submit attempt,
            // consistent with the other folder modals.
            setTouched(true);
            if (!isValid) return;
            onSubmit(value, isSharedChecked);
          }
        }
      >
        <ModalDialogHeader textAlign="start">
          {t(getHeaderKey(), {
            targetParentFolderName: targetParentFolderName || "",
          })}
        </ModalDialogHeader>
        <ModalDialogBody textAlign="start">
          <Field
            label={t("labels.name")}
            inputId="create-folder"
            isRequired={true}
            error={errorMessage}
            renderInput={(inputProps) => (
              <TextInput
                {...inputProps}
                aria-describedby={inputProps.ariaDescribedBy}
                // eslint-disable-next-line jsx-a11y/no-autofocus -- intentionally focus the input when the modal opens
                autoFocus={true}
                id="create-folder"
                isRequired={true}
                layout="block"
                isFullWidth={true}
                maxLength={255}
                onBlur={() => setTouched(true)}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setTouched(true);
                  setValue(event.target?.value || "");
                }}
                placeholder={t("placeholders.folder-name")}
                rows={3}
              />
            )}
          />
          <Text tag="p" type="SmallPrint1" color="GrayWarm9">
            {t("descriptions.character-limit")}
          </Text>

          {isSharedSelectable && !isMPFEnabled && (
            <div className={styles.sharedCheckbox}>
              <Checkbox
                isSelected={isSharedChecked}
                value="isShared"
                name="isShared"
                onChange={(event: any) => {
                  setIsSharedChecked(event.target.checked);
                  emit({
                    optionSelected: FolderActionInteractionOption.CreateFolder,
                    interactionRawText: FolderTypeQueryState.Shared,
                  });
                }}
              >
                {t("labels.share-with-teammates")}
              </Checkbox>
            </div>
          )}
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button
            isDisabled={!isValid}
            isPending={onSubmitLoading}
            onPress={() => onSubmit(value, isSharedChecked)}
            type="button"
          >
            {t("buttons.save")}
          </Button>
        </ModalDialogFooter>
      </div>
    </ModalDialogRoot>
  );
}
export default function FolderCreationModal({
  isOpen,
  isShared,
  isSharedSelectable,
  setIsOpen,
  targetParentFolderName,
  parentId,
}: Props) {
  const t = useTranslations("folder-creation-modal");

  const { createNotification } = useNotification();
  const { create, isLoading: createFolderLoading } = useCreateFolder();
  const { createSubfolder, isLoading: createSubfolderLoading } =
    useCreateSubfolder();
  const { push } = useRouter();

  // This callback needs to happen outside of the modal so that it can
  // still display a notification when the request completes after the modal has closed.
  const submit = (name: string, isShared: boolean) => {
    if (parentId) {
      // If parentId is provided, create a subfolder (root folder, then adopt it as a child)
      createSubfolder(
        { name, isShared, parentId },
        {
          onSuccess: (data) => {
            createNotification({
              message: (
                <Text>{t("messages.folder-created", { name: name })}</Text>
              ),
              type: "success",
            });

            // Redirect to the newly created subfolder
            // The first element in the successes array is the newly created folder
            const folderId = data[0].id;

            push(getFolderUrl(folderId));
          },
          onError: (error: any) => {
            createNotification({
              title: t("errors.unable-to-create", { name }),
              message: getMessageFromNetworkError(error),
              type: "error",
            });
          },
          onSettled: () => {
            setIsOpen(false);
          },
        },
      );
    } else {
      // If parentId is not provided, create root folder
      create(
        { name, isShared },
        {
          onSuccess: ({ id }) => {
            createNotification({
              message: (
                <Text>{t("messages.folder-created", { name: name })}</Text>
              ),
              type: "success",
            });

            push(getFolderUrl(id));
          },
          onError: (error: any) => {
            createNotification({
              title: t("errors.unable-to-create", { name }),
              message: getMessageFromNetworkError(error),
              type: "error",
            });
          },
          onSettled: () => {
            setIsOpen(false);
          },
        },
      );
    }
  };

  // Do not display the modal if isOpen==false
  if (!isOpen) {
    return;
  }

  return (
    <FolderCreationModalContent
      isOpen={isOpen}
      isShared={isShared}
      isSharedSelectable={isSharedSelectable}
      onSubmit={submit}
      setIsOpen={setIsOpen}
      targetParentFolderName={targetParentFolderName}
      onSubmitLoading={createSubfolderLoading || createFolderLoading}
    />
  );
}
